import {
  RESET_FAILED_MESSAGE,
  RESET_FAILURE_MESSAGES,
} from "~shared/constants/auth-reset-messages";

/**
 * ⭐ WHAT THE ENTRY PAGES SAY WHEN SOMETHING GOES WRONG (Gate 1, MC17430).
 * Sign-in never says whether an account exists: a wrong password and an unknown
 * address read the same. Anything that is not a credentials problem gets a
 * generic line — never the library's own text.
 */
export const SIGN_IN_INCORRECT_MESSAGE = "Email or password is incorrect";
export const SIGN_IN_GENERIC_MESSAGE =
  "Something went wrong signing you in. Please try again.";
export const RESET_LINK_PROBLEM_MESSAGE =
  "That reset link is invalid or has expired. Request a new one below.";
export const REGISTER_DUPLICATE_MESSAGE =
  "We couldn't create an account with that email. If it's yours, sign in or reset your password.";
export const REGISTER_GENERIC_MESSAGE =
  "We couldn't create your account. Please try again.";

/** every sign-in failure the library words as a credentials problem */
const CREDENTIAL_CODES = new Set([
  "INVALID_EMAIL_OR_PASSWORD",
  "INVALID_PASSWORD",
  "INVALID_EMAIL",
  "USER_NOT_FOUND",
]);

export function signInErrorMessage(error: {
  code?: string | null;
  status?: number | null;
}): string {
  if (error.code && CREDENTIAL_CODES.has(error.code)) {
    return SIGN_IN_INCORRECT_MESSAGE;
  }
  return SIGN_IN_GENERIC_MESSAGE;
}

/**
 * The set-new-password server answers with one of three FIXED messages; the page
 * shows that message, and anything else (a network failure, an unexpected
 * body) reads as the generic failure — never raw text.
 */
export function resetFailureMessage(serverMessage: string | undefined): string {
  return serverMessage && RESET_FAILURE_MESSAGES.includes(serverMessage)
    ? serverMessage
    : RESET_FAILED_MESSAGE;
}

const DUPLICATE_CODES = new Set([
  "USER_ALREADY_EXISTS",
  "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL",
]);

/**
 * ⭐ REGISTRATION (MC17430): the library's own wording never shows. A duplicate
 * says only that an account could not be made with that email and points at
 * sign-in and reset. Honest limit: with open registration and no email
 * verification, whether an address has an account stays inferable (sign-up
 * succeeds for a new one), so this softens what the page says, it does not hide
 * the fact.
 */
export function signUpErrorMessage(error: {
  code?: string | null;
  status?: number | null;
}): {
  message: string;
  accountExists: boolean;
} {
  const code = error.code ?? "";
  if (DUPLICATE_CODES.has(code)) {
    return { message: REGISTER_DUPLICATE_MESSAGE, accountExists: true };
  }
  switch (code) {
    case "INVALID_EMAIL":
      return {
        message: "Please enter a valid email address.",
        accountExists: false,
      };
    case "PASSWORD_TOO_SHORT":
      return {
        message: "Password must be at least 8 characters.",
        accountExists: false,
      };
    case "PASSWORD_TOO_LONG":
      return {
        message: "That password is too long. Please choose a shorter one.",
        accountExists: false,
      };
    default:
      return { message: REGISTER_GENERIC_MESSAGE, accountExists: false };
  }
}
